/**
 * Agreement Form Modal
 *
 * Two-mode modal:
 *   - `create`: user provides slug + name + initial markdown body.
 *   - `publish-version`: given an existing agreement, user provides new markdown body
 *     that becomes the agreement's next immutable version.
 *
 * Explicit UI copy clarifies that publishing a new version does NOT force users
 * to re-agree — that's a deliberate product decision.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  useCreateAgreement,
  useUpdateAgreementBody,
} from '../../hooks/useAgreementApi';
import type { Agreement } from '../../lib/types/agreement';
import { FormButton } from '../form/FormButton';
import { cn } from '../../lib/utils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'publish-version';
  agreement?: Agreement | null;
  currentBody?: string;
}

const createSchema = z.object({
  slug: z
    .string()
    .regex(
      /^[a-z][a-z0-9-]{1,62}[a-z0-9]$/,
      'Slug must start with a lowercase letter, end with a letter or digit, contain only lowercase letters, digits or hyphens, 3-64 chars.'
    ),
  name: z.string().min(1).max(100),
  body: z.string().min(1, 'Body is required'),
});
type CreateInput = z.infer<typeof createSchema>;

const publishSchema = z.object({
  body: z.string().min(1, 'Body is required'),
});
type PublishInput = z.infer<typeof publishSchema>;

export function AgreementFormModal({
  isOpen,
  onClose,
  mode,
  agreement,
  currentBody,
}: Props) {
  const createAgreement = useCreateAgreement();
  const updateBody = useUpdateAgreementBody();

  const [showDiff, setShowDiff] = useState(false);

  const createForm = useForm<CreateInput>({
    resolver: zodResolver(createSchema),
    defaultValues: { slug: '', name: '', body: '' },
    mode: 'onChange',
  });

  const publishForm = useForm<PublishInput>({
    resolver: zodResolver(publishSchema),
    defaultValues: { body: currentBody || '' },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'create') {
      createForm.reset({ slug: '', name: '', body: '' });
    } else {
      publishForm.reset({ body: currentBody || '' });
      setShowDiff(false);
    }
  }, [isOpen, mode, currentBody, createForm, publishForm]);

  const isLoading = createAgreement.isPending || updateBody.isPending;

  if (!isOpen) return null;

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const onCreate = async (data: CreateInput) => {
    try {
      await createAgreement.mutateAsync(data);
      onClose();
    } catch {
      // handled by hook
    }
  };

  const onPublish = async (data: PublishInput) => {
    if (!agreement) return;
    try {
      await updateBody.mutateAsync({
        slug: agreement.slug,
        input: { body: data.body },
      });
      onClose();
    } catch {
      // handled by hook
    }
  };

  const title =
    mode === 'create' ? 'Create new agreement' : 'Publish new version';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={handleClose}
        />

        <div
          role="dialog"
          aria-modal="true"
          className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {title}
            </h3>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 disabled:opacity-50"
            >
              ✕
            </button>
          </div>

          {mode === 'publish-version' && (
            <p className="mb-4 rounded-md bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 p-3 text-xs text-amber-900 dark:text-amber-100">
              Publishing a new version creates an immutable version doc. Users
              are <strong>not</strong> forced to re-agree — existing responses
              stay bound to their original version. If you need users to
              re-agree, create a <strong>new agreement with a new slug</strong>.
            </p>
          )}

          {mode === 'create' ? (
            <form
              onSubmit={createForm.handleSubmit(onCreate)}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Slug *
                </label>
                <input
                  {...createForm.register('slug')}
                  placeholder="terms-of-service"
                  disabled={isLoading}
                  className={cn(
                    'block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 sm:text-sm font-mono',
                    createForm.formState.errors.slug &&
                      'border-red-300 focus:border-red-500 focus:ring-red-500'
                  )}
                />
                {createForm.formState.errors.slug && (
                  <p className="mt-1 text-sm text-red-600">
                    {createForm.formState.errors.slug.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Permanent identifier. Cannot be changed after creation.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name *
                </label>
                <input
                  {...createForm.register('name')}
                  placeholder="Terms of Service"
                  disabled={isLoading}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Body (Markdown) *
                </label>
                <textarea
                  {...createForm.register('body')}
                  rows={14}
                  disabled={isLoading}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-xs sm:text-sm"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <FormButton
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  Cancel
                </FormButton>
                <FormButton type="submit" isLoading={isLoading}>
                  Create agreement
                </FormButton>
              </div>
            </form>
          ) : (
            <form
              onSubmit={publishForm.handleSubmit(onPublish)}
              className="space-y-4"
            >
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  New body (Markdown) *
                </label>
                <button
                  type="button"
                  className="text-xs text-sky-600 dark:text-sky-400 underline"
                  onClick={() => setShowDiff(v => !v)}
                >
                  {showDiff
                    ? 'Hide current'
                    : 'Show current version side-by-side'}
                </button>
              </div>
              <div
                className={cn(
                  'grid gap-4',
                  showDiff ? 'grid-cols-2' : 'grid-cols-1'
                )}
              >
                {showDiff && (
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Current v{agreement?.current_version}
                    </div>
                    <pre className="whitespace-pre-wrap rounded-md border border-gray-200 dark:border-gray-700 p-2 bg-gray-50 dark:bg-gray-900 text-xs max-h-96 overflow-auto">
                      {currentBody || ''}
                    </pre>
                  </div>
                )}
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    New version (will be v
                    {(agreement?.current_version ?? 0) + 1})
                  </div>
                  <textarea
                    {...publishForm.register('body')}
                    rows={14}
                    disabled={isLoading}
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-xs sm:text-sm"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <FormButton
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  Cancel
                </FormButton>
                <FormButton type="submit" isLoading={isLoading}>
                  Publish new version
                </FormButton>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
